/**
 * The platform's own brand: the company that runs this portal and its creator app (Twiva today).
 *
 * Every place the portal names the platform reads from here: "Powered by Twiva", "the Twiva app",
 * "Twiva wallet", the wordmark logos, the app store links, the page title. To run the portal for
 * another platform, set the VITE_PLATFORM_* values at build time (see .env.example); anything not
 * set stays Twiva.
 *
 * Not to be confused with the *agency's* brand (its name, logo and colours), which each agency
 * sets in Settings → Branding and the portal reads from BrandContext.
 */
// Vite fills import.meta.env; the local API (server/, run by Node) also loads this file through the
// sample data and has none, so it falls back to the defaults there.
const env: Record<string, string | undefined> = import.meta.env ?? {};
const base = env.BASE_URL ?? '/';

/** A build-time setting, or the default when it's unset or blank. */
const pick = (value: string | undefined, fallback: string) => (value && value.trim() ? value.trim() : fallback);

const name = pick(env.VITE_PLATFORM_NAME, 'Twiva');

export const PLATFORM = {
  /** "Twiva": the company, in "Powered by Twiva", "New on Twiva?", "© 2026 Twiva". */
  name,
  /** "Twiva app": the creators' mobile app. */
  appName: pick(env.VITE_PLATFORM_APP_NAME, `${name} app`),
  /** "Twiva wallet": the creators' wallet in the app. */
  walletName: pick(env.VITE_PLATFORM_WALLET_NAME, `${name} wallet`),
  /** "Twiva Pay": the payments brand (wallet cards, "Twiva Pay wallet"). */
  payName: pick(env.VITE_PLATFORM_PAY_NAME, `${name} Pay`),
  /** "Twiva CPaaS": this portal's product name (page title, PDF headers). */
  productName: pick(env.VITE_PLATFORM_PRODUCT_NAME, `${name} CPaaS`),
  /** "cpaas.twiva.com": shown in PDF footers. */
  website: pick(env.VITE_PLATFORM_WEBSITE, 'cpaas.twiva.com'),
  /** Wordmark for light backgrounds. */
  wordmark: pick(env.VITE_PLATFORM_WORDMARK, `${base}brand/twiva-wordmark.png`),
  /** Wordmark for the burgundy panels. */
  wordmarkWhite: pick(env.VITE_PLATFORM_WORDMARK_WHITE, `${base}brand/twiva-wordmark-white.png`),
  /** Square mark, used when the agency has no logo of its own (sidebar). */
  icon: pick(env.VITE_PLATFORM_ICON, `${base}apple-touch-icon.png`),
  /** The creators' app in the stores. */
  appLinks: {
    googlePlay: pick(env.VITE_PLATFORM_GOOGLE_PLAY_URL, 'https://play.google.com/store/apps/details?id=com.twiva.ke'),
    appStore: pick(env.VITE_PLATFORM_APP_STORE_URL, 'https://apps.apple.com/ke/app/twiva/id6444508375'),
  },
} as const;
